// Domain types for the sentiment analysis result. Mirrors the JSON contract
// that the existing analysis backend produces. Keep this file in sync with
// the contract — never widen these unilaterally.

export type Verdict = 'BULLISH' | 'MIXED' | 'BEARISH';

export type DocumentType =
  | 'auto'
  | 'earnings-call'
  | '10-k-mdna'
  | 'credit-report'
  | 'press-release'
  | 'other';

export type Direction = 'A' | 'B' | 'C';

export type EntityType = 'Person' | 'Product' | 'Geography' | 'Segment' | 'Company';

export interface KpiBlock {
  confidence: number; // 0..100
  redFlags: number;
  entities: number;
  sections: number;
}

export interface Signal {
  text: string;
  paragraphRef?: string;
}

export interface Dimension {
  label: string;
  value: number; // 0..1
  qualifier: string; // 'HIGH' / 'ELEVATED' / 'MODERATE' / 'LOW'
  evidence?: { quote: string; ref: string }[];
}

export interface RadarAxis {
  label: string;
  value: number; // 0..1
}

export interface EntityRow {
  name: string;
  type: EntityType;
  verdict: Verdict;
  score: number; // -1..1
  context: string;
}

export interface SectionTimelineRow {
  idx: number;
  title: string;
  tags: string[];
  summary: string;
  range: string; // e.g. "P14–P22"
  verdict: Verdict;
  score: number;
}

export interface QnaFollowUp {
  question: string;
  answerSummary: string;
  pressLevel: 'mild' | 'firm' | 'sharp'; // how hard the analyst pressed back
}

export interface QnaRow {
  id: string;
  analyst: string;
  firm: string;
  analystKind?: 'sell-side' | 'buy-side' | 'sector';
  respondedBy?: string; // executive who answered
  question: string;
  answerSummary: string;
  insight: string;
  verdict: Verdict;
  flags: ('Non-Answer' | 'Deflection' | 'Hedged' | 'Confident')[];
  followUps?: QnaFollowUp[]; // ordered chain of analyst follow-ups
}

export interface NumericalRow {
  metric: string;
  value: string; // formatted ($1.2B, 14.3%, 41.5M)
  numericValue?: number; // raw value for delta math
  period: string;
  direction: 'up' | 'down' | 'star';
  framing: string; // qualitative
  aligned: boolean;
  consensus?: { value: string; numericValue?: number; source: string }; // sell-side mean / median
  beat?: 'beat' | 'miss' | 'in-line';
  nonGaap?: boolean; // flagged as non-GAAP / adjusted figure
  guidanceImplied?: { lo: number; hi: number; metric: string }; // implied guidance range
}

export interface LinguisticIndicator {
  label: string;
  value: string;
  rawValue: number; // 0..1
  tooltip?: string;
}

export interface FingerprintMetric {
  label: string;
  value: number; // 0..1 normalized
  raw?: string; // optional raw string for tooltip
}

export interface SectorLexiconRow {
  name: string;
  matches: number;
  pos: number;
  neg: number;
  risk: number;
  score: number;
  terms?: { positive?: string[]; negative?: string[]; risk?: string[] };
}

export interface RiskQuote {
  quote: string;
  ref: string;
  category: 'guidance' | 'demand' | 'cost' | 'competitive' | 'macro' | 'litigation';
}

export interface VerificationItem {
  label: string;
  status: 'pass' | 'flag' | 'fail';
  note?: string;
}

export interface AnalysisBriefing {
  overall: string;
  dimensionBreakdown: { label: string; copy: string }[];
  entityLandscape: string;
  trajectory: string;
  linguisticProfile: string;
}

export interface SpeakerScore {
  name: string;
  role: string; // 'CEO', 'CFO', 'IR', 'Operator'
  score: number; // -1..1
  utterances: number;
  hedgeDensity: number; // 0..1
  forwardModalDensity: number; // 0..1
  qualifier: 'HIGH' | 'ELEVATED' | 'MODERATE' | 'LOW';
  delta?: number; // change vs same speaker prior quarter
  highlight?: string; // one-line callout
}

export interface CohortStat {
  label: string; // 'Tone & Confidence'
  value: number; // 0..1 or -1..1
  cohortMean: number;
  cohortP10: number;
  cohortP90: number;
  percentile: number; // 0..100, where this doc lands in the cohort
}

export interface CohortBlock {
  peerSet: string; // 'Consumer Hardware · Q2 2026'
  peerCount: number;
  windowLabel: string; // 'Last 30 days'
  stats: CohortStat[];
  overallPercentile: number; // verdict-score percentile
}

export interface PriorReference {
  slug: string;
  title: string;
  date: string;
  verdict: Verdict;
  score: number;
  confidence: number;
  // Selected dimension scores by label, for diff math
  dimensionScores: Record<string, number>;
  speakerScores?: Record<string, number>;
}

export interface TailRiskTerm {
  term: string; // 'could', 'may', 'might', 'possibly'
  count: number;
  impliedProbability: number; // 0..1, calibrated estimate
  context: 'guidance' | 'demand' | 'cost' | 'competitive' | 'macro' | 'litigation' | 'other';
}

export interface TailRiskBlock {
  overallScore: number; // 0..1, normalized magnitude of tail risk language
  terms: TailRiskTerm[];
  weightedExposure: number; // -1..1, signed: negative if downside-skewed
  qualifier: 'HIGH' | 'ELEVATED' | 'MODERATE' | 'LOW';
  topQuotes: { quote: string; ref: string; term: string }[];
}

export interface GuidanceItem {
  metric: string;
  rangeLo: number;
  rangeHi: number;
  unit: string; // '%', '$B', etc.
  consensusMid?: number;
  consensusSource?: string;
  delta?: number; // signed midpoint vs consensus, in unit
  framing: 'raise' | 'reaffirm' | 'lower' | 'introduce';
  note?: string;
}

export interface GuidanceBridgeBlock {
  items: GuidanceItem[];
  netVerdict: 'beat' | 'mixed' | 'miss';
  summary: string;
}

/** GICS-ish sector taxonomy used for cohort comparisons. Keep this list in
 *  sync with `netlify/functions/_lib/sectors.ts` on the backend. */
export type Sector =
  | 'consumer-hardware'
  | 'software-services'
  | 'cloud-compute'
  | 'financial-services'
  | 'telecom'
  | 'pharma-biotech'
  | 'energy'
  | 'industrials'
  | 'consumer-staples'
  | 'real-estate'
  | 'materials'
  | 'utilities'
  | 'other';

export const SECTOR_LABELS: Record<Sector, string> = {
  'consumer-hardware': 'Consumer Hardware',
  'software-services': 'Software & Services',
  'cloud-compute': 'Cloud & Compute',
  'financial-services': 'Financial Services',
  'telecom': 'Telecom',
  'pharma-biotech': 'Pharma & Biotech',
  'energy': 'Energy',
  'industrials': 'Industrials',
  'consumer-staples': 'Consumer Staples',
  'real-estate': 'Real Estate',
  'materials': 'Materials',
  'utilities': 'Utilities',
  'other': 'Other',
};

export interface AnalysisResult {
  slug: string;
  title: string;
  date: string; // ISO
  documentType: DocumentType;
  /** Ticker / company short identifier for prior-quarter lookups (e.g. "AAPL"). */
  ticker?: string;
  /** Calendar period the document covers, normalised to `YYYY-Qn` (quarterly)
   *  or `YYYY-FY` (annual). Drives cohort + prior-quarter joins. */
  period?: string;
  /** Sector classification for peer cohort grouping. */
  sector?: Sector;
  verdict: Verdict;
  score: number; // -1..1
  confidence: number; // 0..100
  /** Editorial-grade headline produced by Pass 3 — sentence-cased, 8-14 words,
   *  reads like a Bloomberg headline. Used by Direction C VerdictHeader. */
  headline?: string;
  summary: string;
  briefing: AnalysisBriefing;
  kpi: KpiBlock;
  bullSignals: Signal[];
  bearSignals: Signal[];
  dimensions: Dimension[];
  radar: RadarAxis[];
  entities: EntityRow[];
  sections: SectionTimelineRow[];
  qna: QnaRow[];
  numerical: NumericalRow[];
  linguisticIndicators: LinguisticIndicator[];
  fingerprint: FingerprintMetric[];
  sectorLexicon: SectorLexiconRow[];
  riskQuotes: RiskQuote[];
  verification: VerificationItem[];
  advanced: { key: string; value: string }[];

  // Bucket 1 additions
  speakers?: SpeakerScore[];
  cohort?: CohortBlock;
  priorQuarter?: PriorReference;
  tailRisk?: TailRiskBlock;
  guidanceBridge?: GuidanceBridgeBlock;
}

export interface HistoryItem {
  id: string;
  slug: string;
  title: string;
  date: string;
  verdict: Verdict;
}

export type SectionId =
  | 'overview'
  | 'qoq'
  | 'peers'
  | 'speakers'
  | 'briefing'
  | 'dimensions'
  | 'guidance'
  | 'charts'
  | 'sections'
  | 'qna'
  | 'numerical'
  | 'linguistic'
  | 'lexicon'
  | 'tailrisk'
  | 'risk'
  | 'verification'
  | 'summary'
  | 'advanced';
